import { AbsolutePath, Path } from "@kform/core";
import * as React from "react";

import { FormContext } from "../contexts/FormContext";
import { NoFormContextError } from "../utils/errors";

/**
 * Resolves a given path against the current form path or against
 * {@link currentPath} when provided. The returned absolute path has a stable
 * identity.
 * @param path Path to resolve against the current path.
 * @param currentPath Optional current path argument. When provided, this path
 * is used instead of the form context's current path.
 * @returns Resolved absolute path with a stable identity.
 */
export function useResolvedPath(
  path: Path | string = Path.CURRENT,
  currentPath?: AbsolutePath,
): AbsolutePath {
  const formContext = React.useContext(FormContext);
  currentPath ??= formContext?.currentPath;
  if (currentPath == null) {
    throw new NoFormContextError();
  }

  const stableAbsolutePathRef = React.useRef<AbsolutePath>();
  const absolutePath = React.useMemo(
    () => currentPath.resolve(path),
    [currentPath, path],
  );
  if (!absolutePath.equals(stableAbsolutePathRef.current)) {
    stableAbsolutePathRef.current = absolutePath;
  }
  return stableAbsolutePathRef.current!;
}
